import { Injectable } from '@angular/core';
import { ActionBackOfficeResponse } from '../../models/api.model';
import {
  ApprovalConfigApprover,
  ApprovalConfigApproverPayload,
  ApprovalConfigDetail,
  ApprovalConfigListRow,
  ApprovalConfigStep,
  ApprovalConfigStepPayload,
  ApprovalConfigTableDto,
  SaveApprovalConfigPayload,
} from '../../models/approval/approval-config.model';

@Injectable({ providedIn: 'root' })
export class MapperApprovalConfigService {
  mapTable(dto: ApprovalConfigTableDto): {
    items: ApprovalConfigListRow[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as ApprovalConfigTableDto & Record<string, unknown>;
    const rows = (dto.data ?? raw['Data'] ?? []) as ApprovalConfigListRow[];
    return {
      items: rows.map((row) => this.mapListRow(row as ApprovalConfigListRow & Record<string, unknown>)),
      total: Number(dto.total ?? raw['Total'] ?? 0),
      page: Number(dto.page ?? raw['Page'] ?? 1),
      pageSize: Number(dto.pageSize ?? raw['PageSize'] ?? 20),
      totalPages: Number(dto.totalPages ?? raw['TotalPages'] ?? 0),
    };
  }

  mapListRow(row: ApprovalConfigListRow & Record<string, unknown>): ApprovalConfigListRow {
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      name: String(row.name ?? row['Name'] ?? ''),
      menuListId: Number(row.menuListId ?? row['MenuListId'] ?? 0),
      menuName: String(row.menuName ?? row['MenuName'] ?? ''),
      enable: Boolean(row.enable ?? row['Enable'] ?? false),
      maxLevelCount: Number(row.maxLevelCount ?? row['MaxLevelCount'] ?? 1),
      stepCount: Number(row.stepCount ?? row['StepCount'] ?? 0),
      siteName: String(row.siteName ?? row['SiteName'] ?? ''),
      departmentName: String(row.departmentName ?? row['DepartmentName'] ?? ''),
    };
  }

  mapDetail(dto: ApprovalConfigDetail & Record<string, unknown>): ApprovalConfigDetail {
    const stepsRaw = (dto.steps ?? dto['Steps'] ?? []) as ApprovalConfigStep[];
    const approversRaw = (dto.approvers ?? dto['Approvers'] ?? []) as ApprovalConfigApprover[];
    return {
      id: Number(dto.id ?? dto['Id'] ?? 0),
      name: String(dto.name ?? dto['Name'] ?? ''),
      menuListId: Number(dto.menuListId ?? dto['MenuListId'] ?? 0),
      menuName: String(dto.menuName ?? dto['MenuName'] ?? ''),
      enable: Boolean(dto.enable ?? dto['Enable'] ?? true),
      maxLevelCount: Number(dto.maxLevelCount ?? dto['MaxLevelCount'] ?? 1),
      triggerOn: String(dto.triggerOn ?? dto['TriggerOn'] ?? 'create'),
      siteId: dto.siteId != null ? Number(dto.siteId ?? dto['SiteId']) : null,
      siteName: String(dto.siteName ?? dto['SiteName'] ?? ''),
      departmentId: dto.departmentId != null ? Number(dto.departmentId ?? dto['DepartmentId']) : null,
      departmentName: String(dto.departmentName ?? dto['DepartmentName'] ?? ''),
      description: String(dto.description ?? dto['Description'] ?? ''),
      steps: stepsRaw.map((s) => this.mapStep(s as ApprovalConfigStep & Record<string, unknown>)),
      approvers: approversRaw.map((a) => this.mapApprover(a as ApprovalConfigApprover & Record<string, unknown>)),
    };
  }

  mapStep(step: ApprovalConfigStep & Record<string, unknown>): ApprovalConfigStep {
    return {
      id: step.id != null ? Number(step.id ?? step['Id']) : undefined,
      stepNumber: Number(step.stepNumber ?? step['StepNumber'] ?? 1),
      stepName: String(step.stepName ?? step['StepName'] ?? ''),
      approverType: String(step.approverType ?? step['ApproverType'] ?? 'role'),
      userId: step.userId != null ? Number(step.userId ?? step['UserId']) : null,
      userName: String(step.userName ?? step['UserName'] ?? ''),
      roleId: step.roleId != null ? Number(step.roleId ?? step['RoleId']) : null,
      roleName: String(step.roleName ?? step['RoleName'] ?? ''),
      departmentId: step.departmentId != null ? Number(step.departmentId ?? step['DepartmentId']) : null,
      departmentName: String(step.departmentName ?? step['DepartmentName'] ?? ''),
      nextApproverMode: String(step.nextApproverMode ?? step['NextApproverMode'] ?? 'end'),
    };
  }

  mapApprover(item: ApprovalConfigApprover & Record<string, unknown>): ApprovalConfigApprover {
    return {
      id: item.id != null ? Number(item.id ?? item['Id']) : undefined,
      fromStepNumber: Number(item.fromStepNumber ?? item['FromStepNumber'] ?? 1),
      forStepNumber: Number(item.forStepNumber ?? item['ForStepNumber'] ?? 2),
      userId: item.userId != null ? Number(item.userId ?? item['UserId']) : null,
      userName: String(item.userName ?? item['UserName'] ?? ''),
      roleId: item.roleId != null ? Number(item.roleId ?? item['RoleId']) : null,
      roleName: String(item.roleName ?? item['RoleName'] ?? ''),
      departmentId: item.departmentId != null ? Number(item.departmentId ?? item['DepartmentId']) : null,
      departmentName: String(item.departmentName ?? item['DepartmentName'] ?? ''),
      isDefault: Boolean(item.isDefault ?? item['IsDefault'] ?? false),
      enable: Boolean(item.enable ?? item['Enable'] ?? true),
    };
  }

  mapToSavePayload(detail: ApprovalConfigDetail, userId: number, menuId: number): SaveApprovalConfigPayload {
    return {
      userId,
      menuId,
      id: detail.id > 0 ? detail.id : null,
      name: detail.name.trim(),
      menuListId: detail.menuListId,
      enable: detail.enable,
      maxLevelCount: detail.maxLevelCount,
      triggerOn: detail.triggerOn || 'create',
      siteId: detail.siteId,
      departmentId: detail.departmentId,
      description: detail.description || null,
      steps: detail.steps.map((s) => this.mapStepToPayload(s)),
      approvers: detail.approvers.map((a) => this.mapApproverToPayload(a)),
    };
  }

  mapStepToPayload(step: ApprovalConfigStep): ApprovalConfigStepPayload {
    return {
      stepNumber: step.stepNumber,
      stepName: step.stepName || null,
      approverType: step.approverType,
      userId: step.approverType === 'user' ? step.userId : null,
      roleId: step.approverType === 'role' ? step.roleId : null,
      departmentId: step.approverType === 'department' ? step.departmentId : null,
      nextApproverMode: step.nextApproverMode,
    };
  }

  mapApproverToPayload(item: ApprovalConfigApprover): ApprovalConfigApproverPayload {
    return {
      fromStepNumber: item.fromStepNumber,
      forStepNumber: item.forStepNumber,
      userId: item.userId,
      roleId: item.roleId,
      departmentId: item.departmentId,
      isDefault: item.isDefault,
      enable: item.enable,
    };
  }

  createEmptyDetail(): ApprovalConfigDetail {
    return {
      id: 0,
      name: '',
      menuListId: 0,
      menuName: '',
      enable: true,
      maxLevelCount: 1,
      triggerOn: 'create',
      siteId: null,
      siteName: '',
      departmentId: null,
      departmentName: '',
      description: '',
      steps: [
        {
          stepNumber: 1,
          stepName: 'ชั้นที่ 1',
          approverType: 'role',
          userId: null,
          userName: '',
          roleId: null,
          roleName: '',
          departmentId: null,
          departmentName: '',
          nextApproverMode: 'end',
        },
      ],
      approvers: [],
    };
  }

  mapActionResponse(res: unknown): ActionBackOfficeResponse {
    const raw = (res ?? {}) as Record<string, unknown>;
    return {
      isSuccess: Boolean(raw['isSuccess'] ?? raw['IsSuccess'] ?? false),
      message: String(raw['message'] ?? raw['Message'] ?? ''),
      messageEn: String(raw['messageEn'] ?? raw['MessageEn'] ?? ''),
      code: String(raw['code'] ?? raw['Code'] ?? ''),
      IdReturn: Number(raw['IdReturn'] ?? raw['idReturn'] ?? 0),
    };
  }
}
